import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Autocomplete, TextField, Typography, createFilterOptions } from "@mui/material";
import type { Perimetre } from "models";
import CompetenceChip from "components/Typography/CompetenceChip";
import { stringUtil } from "@sdeapps/react-core";
import InputSkeleton from "components/InputSkeleton";
import { perimetresService } from "services";
import { useErrorHandler } from "utils/errorHandling";
import type { FieldError, RefCallBack } from "react-hook-form";

const filterOptions = createFilterOptions({
  trim: true,
  stringify: (option: Perimetre) => stringUtil.normalize(option.libelle),
});

interface PerimetreAutoCompleteProps {
  label?: string;
  perimetres?: Array<Perimetre>;
  onChange: (selectedPerimetre?: Perimetre | null) => void;
  isLoading?: boolean;
  noOptionsText?: string;

  value?: Perimetre | null;
  inputRef?: RefCallBack;
  error?: FieldError;
}

function PerimetreAutoComplete({
  label,
  perimetres: overridePerimetres,
  onChange,
  isLoading: overrideIsLoading = false,
  noOptionsText,

  value,
  inputRef,
  error,
}: Readonly<PerimetreAutoCompleteProps>): ReactNode {
  const [perimetres, setPerimetres] = useState<Array<Perimetre>>([]);
  const { catchErrors, isLoading } = useErrorHandler({
    defaultIsLoading: overridePerimetres == null,
  });

  useEffect(() => {
    void catchErrors(async function getPerimetres(): Promise<void> {
      setPerimetres(
        (overridePerimetres ?? (await perimetresService.getAll())).toSorted((p1, p2) =>
          stringUtil.normalize(p1.libelle).localeCompare(stringUtil.normalize(p2.libelle))
        )
      );
    });
  }, [catchErrors, overridePerimetres]);

  if (overrideIsLoading || isLoading) {
    return <InputSkeleton />;
  }

  return (
    <Autocomplete
      fullWidth
      autoHighlight
      openOnFocus
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      options={perimetres}
      filterOptions={filterOptions}
      onChange={(_, newValue) => {
        onChange(newValue);
      }}
      value={value}
      isOptionEqualToValue={(v1: Perimetre, v2: Perimetre): boolean => v1.id === v2.id}
      getOptionLabel={(perimetre: Perimetre) => `${perimetre.libelle} - ${perimetre.competence}`}
      renderOption={(props, option) => (
        <Typography component="li" {...props} sx={{ display: "block" }} key={option.id}>
          <CompetenceChip sx={{ marginRight: 1 }} competence={option.competence} /> {option.libelle}
        </Typography>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? "Périmètre *"}
          placeholder="Nom du Périmètre"
          inputRef={inputRef}
          error={error != null}
          helperText={error?.message}
        />
      )}
      noOptionsText={noOptionsText}
      sx={{ background: "white" }}
    />
  );
}

export default PerimetreAutoComplete;
